import { CURRENT_FIGHT_STATUSES, FightStatus, GameType, IFight, IFightChartTotal, IPagination } from '@/utils/index.model';
import { deepMerge, getData, keysToCamel } from '@/utils/helpers';
import { API_URLS } from '@/utils/constant';
import { sortBy } from 'lodash';
import { sendWsRequest } from '@/utils/websocket';

interface IFightState {
  currentFight: IFight;
  fights: IFight[];
  chartFights: IFight[];
  chartFightsTotal: IFightChartTotal;
  fightLoading: boolean;
  fightPagination: IPagination;
  currentFightStatus: {
    status: string;
    fightNumber: number;
    arenaId: string | number;
  } | null;
}

const FIGHT_DATA: IFight = {
  id: '',
  fightNumber: 0,
  status: '',
  result: '',
  streamUrl: '',
  replayUrl: '',
  isBetsPaused: false,
  confirmedDateTime: '',
  selections: [
    {
      name: '',
      displayName: '',
      odds: 0,
      totalBetAmount: 0,
      totalBetCount: 0,
    },
  ],
  matchDay: {
    id: '',
    code: '',
    startDatetime: '',
    fightType: '',
    arena: {
      id: '',
      name: {
        default: '',
        vn: '',
      },
      country: '',
      code: '',
      currentMatchDayId: '',
      fightStatus: '',
      isReverse: false,
    },
  },
};

export const state = () => ({
  currentFight: { ...FIGHT_DATA },
  fights: [],
  chartFights: [],
  chartFightsTotal: {
    playerCount: 0,
    bankerCount: 0,
    drawCount: 0,
  },
  fightLoading: false,
  fightPagination: { page: 1, pageSize: 20 },
  currentFightStatus: null,
});

export const mutations = {
  UPDATE_FIGHT(stateData: IFightState, fight: Partial<IFight>): void {
    // When a genuinely new fight arrives (different ID), start from the blank template
    // rather than merging onto the old fight — prevents stale result/status leaking through.
    const base = fight.id && fight.id !== stateData.currentFight.id
      ? { ...FIGHT_DATA }
      : { ...stateData.currentFight };
    stateData.currentFight = deepMerge(base, fight);
  },
  RESET_FIGHT(stateData: IFightState) {
    stateData.currentFight = { ...FIGHT_DATA };
  },
  RESET_FIGHTS(stateData: IFightState) {
    stateData.fights = [];
  },
  INIT_FIGHTS(stateData: IFightState, fights: IFight[]) {
    stateData.fights = [...fights];
  },
  INSERT_FIGHTS(stateData: IFightState, fights: IFight[]) {
    stateData.fights = [...stateData.fights, ...fights];
  },
  INSERT_CHART_FIGHTS(stateData: IFightState, fights: IFight[]) {
    stateData.chartFights = [...fights];
  },
  INSERT_CHART_FIGHTS_TOTAL(stateData: IFightState, fightTotal: IFightChartTotal) {
    stateData.chartFightsTotal = { ...fightTotal };
  },
  UPDATE_PAGINATION(stateData: IFightState, pagination: IPagination): void {
    stateData.fightPagination = { ...pagination };
  },
  RESET_PAGINATION(stateData: IFightState) {
    stateData.fightPagination = { page: 1, pageSize: 20 };
  },
  UPDATE_LOADING(stateData: IFightState, loading: boolean): void {
    stateData.fightLoading = loading;
  },
  SET_FIGHT_STATUS(stateData: IFightState, status: any) {
    stateData.currentFightStatus = status;
  },
};

export const actions = {
  updateFight(context: any, fight: Partial<IFight>): void {
    context.commit('UPDATE_FIGHT', fight);
  },
  resetFight(context: any): void {
    context.commit('RESET_FIGHT');
  },
  resetFights(context: any): void {
    context.commit('RESET_FIGHTS');
  },
  getCurrentFight(
    context: any,
    paramsPartial: {
      arena_id: string;
    }
  ): void {
    const { rootGetters } = context;
    const oddsType = rootGetters['general/oddsType'];
    const params = new URLSearchParams();
    const { arena_id } = paramsPartial;
    params.append('arena_id', arena_id);
    CURRENT_FIGHT_STATUSES.forEach((status) => params.append('status', status));
    params.append('odds_type', oddsType);
    const token = rootGetters['general/token'];
    getData(API_URLS.FIGHT_HISTORY, token, params)
      .then((res) => {
        if (res.length) {
          // Newest first: an older fight still waiting to be declared ("ended")
          // is live too, and the endpoint's order alone could hand that back
          // instead of the fight actually running.
          const tempList = sortBy(
            res.filter((f: IFight) => Number(FightStatus[f.status]) > 0 && Number(FightStatus[f.status]) < 6),
            (f: any) => Number(f.fight_number)
          ).reverse();
          context.commit('UPDATE_FIGHT', keysToCamel(tempList[0]));
        } else {
          context.dispatch('bets/resetMyBets', null, { root: true });
        }
      })
      .catch((e) => console.log(e));
  },
  getFightsForChart(context: any, matchDayId: string): void {
    const { rootGetters } = context;
    const token = rootGetters['general/token'];
    const oddsType = rootGetters['general/oddsType'];
    const payload = {
      matchDayId: matchDayId,
      oddsType: oddsType,
    };
    sendWsRequest(token, GameType.fight_results, payload);

    // context.commit('UPDATE_LOADING', true);
    // getData(API_URLS.FIGHT_RESULT, token, { match_day_id: matchDayId })
    //   .then((res) => {
    //     context.commit('INSERT_CHART_FIGHTS', keysToCamel(res));
    //   })
    //   .catch((e) => console.log(e))
    //   .finally(() => context.commit('UPDATE_LOADING', false));
    // getData(`${API_URLS.FIGHT_RESULT}totals`, token, { match_day_id: matchDayId })
    //   .then((res) => {
    //     context.commit('INSERT_CHART_FIGHTS_TOTAL', keysToCamel(res));
    //   })
    //   .catch((e) => console.log(e));
  },
  updateFightsPage(context: any, pagination: IPagination): void {
    context.commit('UPDATE_PAGINATION', pagination);
  },
  resetFightsPage(context: any): void {
    context.commit('RESET_PAGINATION');
  },

  updateFightStatus({ commit }: { commit: any }, fightStatus: any): void {
    commit('SET_FIGHT_STATUS', fightStatus);
  },
};

export const getters = {
  fight: (stateData: any): IFight => {
    return stateData.currentFight;
  },
  fights: (stateData: any): IFight[] => {
    return stateData.fights;
  },
  chartFights: (stateData: any): IFight[] => {
    return stateData.chartFights;
  },
  chartFightsTotal: (stateData: any): IFightChartTotal => {
    return stateData.chartFightsTotal;
  },
  fightLoading: (stateData: any): boolean => {
    return stateData.fightLoading;
  },
  fightPagination: (stateData: any): IPagination => {
    return stateData.fightPagination;
  },
  currentFightStatus: (stateData: any): { status: string; fightNumber: number; arenaId: string | number } | null => {
    return stateData.currentFightStatus;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
